import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import QuestionRenderer from "@/components/QuestionRenderer";
import { ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";

const AdminInterviewer = () => {
  const { t, lang, pickI18n } = useLang();
  const [forms, setForms] = useState([]);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/questionnaires").then((r) => {
      const internals = r.data.filter((q) => q.is_internal && q.is_active);
      setForms(internals);
      if (internals.length > 0) setActive(internals[0]);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!active) return;
    setLoading(true);
    try {
      await api.post("/responses", { questionnaire_id: active.id, answers, interviewer_notes: notes });
      toast.success(t("common.saved"));
      setAnswers({});
      setNotes("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.interviewer")}</h1>
        </div>
        <span className="tsw-chip bg-amber-50 text-amber-700 border border-amber-200">
          <ShieldCheck className="w-3 h-3 me-1" /> {t("admin.onlyStaff")}
        </span>
      </div>
      <p className="text-sm text-slate-500">{t("admin.interviewerNote")}</p>

      {forms.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {forms.map((f) => (
            <button key={f.id} onClick={() => { setActive(f); setAnswers({}); }} className={`tsw-chip cursor-pointer ${active?.id === f.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`interviewer-form-${f.id}`}>
              {pickI18n(f, "title") || f.title_en}
            </button>
          ))}
        </div>
      )}

      {active && (
        <form onSubmit={submit} className="tsw-card p-6 space-y-5" data-testid="interviewer-form">
          {active.questions.map((q) => (
            <QuestionRenderer key={q.id} question={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
          ))}
          <div>
            <label className="tsw-label">Internal notes</label>
            <textarea className="tsw-input min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="interviewer-notes" />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="tsw-btn-primary" disabled={loading} data-testid="interviewer-submit">
              <Send className="w-4 h-4" /> {loading ? t("common.sending") : t("common.submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminInterviewer;
